import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Property, Tenant, Lease, MaintenanceRequest, RentPayment } from '../types/properties';
import { TenantInfo } from '../components/properties/TenantInfo';
import { LeaseForm } from '../components/properties/LeaseForm';
import { RentTracker } from '../components/properties/RentTracker';
import { MaintenanceRequestCard } from '../components/maintenance/MaintenanceRequestCard';
import { MaintenanceRequestForm } from '../components/maintenance/MaintenanceRequestForm';
import { Modal } from '../components/Modal';
import { Building, MapPin, ArrowLeft, Home, FileText, Wrench, DollarSign, Users, Plus } from 'lucide-react';
import { formatCurrency } from '../lib/format';

export function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const [property, setProperty] = useState<Property | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceRequest[]>([]);
  const [payments, setPayments] = useState<RentPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [
        { data: pData }, { data: tData }, { data: lData }, { data: mData }, { data: rData }
      ] = await Promise.all([
        supabase.from('properties').select('*').eq('id', id).single(),
        supabase.from('tenants').select('*').eq('property_id', id).order('created_at', { ascending: false }),
        supabase.from('leases').select('*').eq('property_id', id).order('created_at', { ascending: false }),
        supabase.from('maintenance_requests').select('*').eq('property_id', id).order('created_at', { ascending: false }),
        supabase.from('rent_payments').select('*').eq('property_id', id).order('payment_date', { ascending: false })
      ]);

      setProperty(pData);
      setTenants(tData || []);
      setLeases(lData || []);
      setMaintenance(mData || []);
      setPayments(rData || []);
    } catch (error) {
      console.error('Error fetching property details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const activeTenant = tenants.find(t => t.status === 'active') || null;
  const activeLease = leases.find(l => l.status === 'active') || null;

  if (isLoading || !property) {
    return <div className="p-8 text-[var(--t-text-muted)]">Loading property details...</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="px-8 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/properties')} className="p-2 bg-[var(--t-surface-hover)] rounded-xl text-[var(--t-text-muted)] hover:text-white transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-[var(--t-text)]">{property.address}</h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[var(--t-surface-hover)] text-[var(--t-text-muted)]">
                {property.status.replace(/-/g, ' ')}
              </span>
            </div>
            <p className="text-sm text-[var(--t-text-muted)] mt-1 flex items-center gap-1.5">
              <MapPin size={14} /> {property.city}, {property.state} {property.zip}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-8 flex gap-6 mt-2 overflow-x-auto custom-scrollbar">
          {[
            { id: 'overview', label: 'Overview', icon: Home },
            { id: 'tenant', label: 'Tenant', icon: Users },
            { id: 'lease', label: 'Lease', icon: FileText },
            { id: 'maintenance', label: 'Maintenance', icon: Wrench },
            { id: 'financials', label: 'Financials', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-semibold transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[var(--t-primary)] border-[var(--t-primary)]'
                  : 'text-[var(--t-text-muted)] border-transparent hover:text-[var(--t-text)]'
              }`}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        <div className="max-w-5xl mx-auto">

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 flex flex-col gap-6">
                <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl overflow-hidden h-80 relative group">
                  {property.photo_url ? (
                    <img src={property.photo_url} alt={property.address} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[var(--t-text-muted)] gap-2 bg-[var(--t-surface-hover)]">
                      <Building size={48} className="opacity-20 mb-2" />
                      <span className="text-sm font-medium">No Photo Available</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-5">
                  <h3 className="text-xs font-bold text-[var(--t-text-muted)] uppercase tracking-wider mb-4">Property Specs</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between">
                      <span className="text-[var(--t-text-muted)] text-sm">Type</span>
                      <span className="text-[var(--t-text)] font-medium capitalize">{property.property_type.replace('-', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--t-text-muted)] text-sm">Beds / Baths</span>
                      <span className="text-[var(--t-text)] font-medium">{property.beds || '-'} / {property.baths || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--t-text-muted)] text-sm">Square Feet</span>
                      <span className="text-[var(--t-text)] font-medium">{property.sqft?.toLocaleString() || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--t-text-muted)] text-sm">Year Built</span>
                      <span className="text-[var(--t-text)] font-medium">{property.year_built || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[var(--t-primary-dim)] border border-[var(--t-primary)]/20 rounded-2xl p-5">
                  <h3 className="text-xs font-bold text-[var(--t-primary)] uppercase tracking-wider mb-2">Current Rent</h3>
                  <div className="text-3xl font-black text-[var(--t-primary)]">
                    {activeLease ? formatCurrency(activeLease.monthly_rent) : '—'}
                  </div>
                  <p className="text-xs text-[var(--t-text-muted)] mt-1">
                    {activeLease ? `Lease ends ${new Date(activeLease.end_date).toLocaleDateString()}` : 'No active lease'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tenant' && (
            <TenantInfo tenant={activeTenant} />
          )}

          {activeTab === 'lease' && (
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Lease Agreements</h2>
                <button
                  onClick={() => setShowLeaseModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors"
                >
                  <Plus size={16} /> New Lease
                </button>
              </div>

              {leases.length === 0 ? (
                <div className="p-8 text-center text-[var(--t-text-muted)] border-2 border-dashed border-[var(--t-border)] rounded-2xl">
                  No leases found for this property.
                </div>
              ) : (
                <div className="grid gap-4">
                  {leases.map(lease => (
                    <div key={lease.id} className="bg-[var(--t-surface)] border border-[var(--t-border)] p-4 rounded-xl flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white">Lease Agreement</span>
                          <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded ${lease.status === 'active' ? 'bg-[var(--t-success-dim)] text-[var(--t-success)]' : 'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)]'}`}>
                            {lease.status}
                          </span>
                        </div>
                        <div className="text-sm text-[var(--t-text-muted)]">
                          {new Date(lease.start_date).toLocaleDateString()} - {new Date(lease.end_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[var(--t-text)]">{formatCurrency(lease.monthly_rent)}/mo</div>
                        <div className="text-xs text-[var(--t-text-muted)]">Dep: {formatCurrency(lease.security_deposit || 0)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Maintenance Requests</h2>
                <button
                  onClick={() => setShowMaintenanceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--t-surface)] text-[var(--t-text)] border border-[var(--t-border)] text-sm font-semibold rounded-xl hover:bg-[var(--t-surface-hover)] transition-colors"
                >
                  <Plus size={16} /> New Request
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {maintenance.map(req => (
                  <MaintenanceRequestCard key={req.id} request={req} />
                ))}
                {maintenance.length === 0 && (
                  <div className="col-span-2 p-8 text-center text-[var(--t-text-muted)] border-2 border-dashed border-[var(--t-border)] rounded-2xl">
                    No maintenance requests for this property.
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="flex flex-col gap-6">
              <h2 className="text-lg font-bold text-white">Rent Ledger</h2>
              <RentTracker payments={payments} />
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      <Modal isOpen={showLeaseModal} onClose={() => setShowLeaseModal(false)} title="Create New Lease" maxWidth="max-w-2xl">
        <LeaseForm propertyId={property.id} tenants={tenants} onClose={() => setShowLeaseModal(false)} onSuccess={() => { setShowLeaseModal(false); fetchData(); }} />
      </Modal>

      <Modal isOpen={showMaintenanceModal} onClose={() => setShowMaintenanceModal(false)} title="Submit Maintenance Request" maxWidth="max-w-xl">
        <MaintenanceRequestForm propertyId={property.id} tenantId={activeTenant?.id} onClose={() => setShowMaintenanceModal(false)} onSuccess={() => { setShowMaintenanceModal(false); fetchData(); }} />
      </Modal>
    </div>
  );
}
